/* src/components/testing/countingSpan/CountingSpanTask.js */
import React, { useEffect, useRef, useCallback, useState } from 'react'; // Import useState
import { useNavigate } from 'react-router-dom';
import useCountingSpanLogic from './hooks/useCountingSpanLogic';
import useCountingSpanTimer from './hooks/useCountingSpanTimer';
import { TASK_PHASES } from '../../../constants/cstConstants';

// Import UI Sub-components
import StimulusDisplay from './StimulusDisplay';
import RecallInterface from './RecallInterface';
import CountingSpanResults from './CountingSpanResults';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ConfirmationModal from '../../ui/ConfirmationModal'; // <<< Import ConfirmationModal
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faForward, faTimes } from '@fortawesome/free-solid-svg-icons'; // <<< Import faTimes for exit icon
import FeedbackButton from '../../feedback/FeedbackButton';

/**
 * Main component orchestrating the Counting Span assessment task.
 * Integrates logic (useCountingSpanLogic), timing (useCountingSpanTimer),
 * and UI components (Stimulus, Recall, Results).
 * Assumes navigation has already occurred and immediately starts the assessment sequence.
 * Includes a development-only skip button and an early exit button.
 * Also renders the FeedbackButton.
 */
const CountingSpanTask = () => {
    const navigate = useNavigate();
    const isDevelopment = process.env.NODE_ENV === 'development';

    // --- State for Exit Confirmation ---
    const [isExitModalOpen, setIsExitModalOpen] = useState(false); // <<< State for exit modal

    // --- Instantiate Hooks ---
    const advancePhaseRef = useRef(() => { console.warn("advancePhase called before logic hook initialized"); });

    // Timer hook
    const { clearStimulusJudgmentTimer, clearRecallTimer } = useCountingSpanTimer(
        null, // currentPhase placeholder
        (...args) => advancePhaseRef.current(...args), // Use ref
        null, // currentTrialIndexInSet placeholder
        null  // setSize placeholder
    );

    // Logic hook
    const {
        // State
        currentPhase, currentSet, currentTrialIndexInSet, currentStimulus,
        isLoading, error, isPractice, setSize,
        testStructure, currentSetIndex, feedback,
        intermediateRecallSequence,

        // Results
        finalScorePCU, finalProcessingAccuracy, flaggedLowAccuracy, isFamiliarization,

        // Actions / Handlers
        startAssessment, recordJudgment, submitRecall,
        advancePhase: actualAdvancePhase, // Get the real function
        handleRecallChange, handleDevSkipTask,
        handleExitAssessment, // <<< Import the new exit handler

    } = useCountingSpanLogic(
        () => { console.log("Task sequence complete callback triggered (after saving results)."); },
        clearStimulusJudgmentTimer,
        clearRecallTimer
    );

    // --- Effect Hooks ---
    // Effect to Start Assessment
    useEffect(() => {
        if (currentPhase === TASK_PHASES.INSTRUCTIONS) {
            console.log("[CountingSpanTask] Component mounted, triggering startAssessment...");
            startAssessment();
        }
    }, [startAssessment, currentPhase]);

    // Update the ref with the actual advancePhase function
    useEffect(() => {
        advancePhaseRef.current = actualAdvancePhase;
    }, [actualAdvancePhase]);

    // Re-run useCountingSpanTimer whenever dependencies change
    useCountingSpanTimer(
        currentPhase,
        actualAdvancePhase,
        currentTrialIndexInSet,
        setSize
    );

    // --- UI Event Handlers ---
    // ... (handleJudgmentClick, handleRecallSubmit remain the same) ...
    const handleJudgmentClick = useCallback((userJudgmentIsOdd) => {
        if (currentPhase !== TASK_PHASES.ODD_EVEN_JUDGMENT || !currentStimulus) {
            console.warn("Judgment click ignored: Incorrect phase or no stimulus.");
            return;
        }
        const correctCount = currentStimulus.targetCount;
        const isCorrect = (correctCount % 2 !== 0) === userJudgmentIsOdd;
        const responseTime = performance.now(); // Use performance.now() for better precision if needed
        recordJudgment(isCorrect, responseTime);
    }, [currentPhase, currentStimulus, recordJudgment]);

    const handleRecallSubmit = useCallback(() => {
         if (currentPhase !== TASK_PHASES.RECALL) {
             console.warn("Recall submission ignored: Incorrect phase.");
             return;
         }
         if (clearRecallTimer) clearRecallTimer(); // Clear timer before submitting logic
         submitRecall();
     }, [currentPhase, submitRecall, clearRecallTimer]);

    // --- Exit Handlers ---
    const openExitModal = useCallback(() => {
        setIsExitModalOpen(true);
    }, []);

    const closeExitModal = useCallback(() => {
        setIsExitModalOpen(false);
    }, []);

    const confirmExit = useCallback(() => {
        closeExitModal(); // Close modal first
        handleExitAssessment(); // Then call the logic hook's exit handler
    }, [closeExitModal, handleExitAssessment]); // Add dependencies


    // --- Render Logic ---
    const renderContent = () => {
        // ... (Loading and Error rendering remains the same) ...
        if (isLoading) {
            const message = currentPhase === TASK_PHASES.SAVING ? "Saving results..." : "Loading assessment...";
            return <LoadingSpinner message={message} />;
        }
        if (error && currentPhase !== TASK_PHASES.RESULTS) {
             return (
                <div className="text-red-600 p-4 border border-red-200 bg-red-50 rounded text-center">
                    <p className="font-semibold mb-2">An Error Occurred</p>
                    <p className="text-sm mb-4">{error}</p>
                    <button
                        onClick={() => navigate('/testing/counting-span')}
                        className="px-4 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                    >
                         Return to Summary
                    </button>
                </div>
             );
        }

        switch (currentPhase) {
            // ... (cases for READY_ASSESSMENT, FIXATION, ODD_EVEN_JUDGMENT, PROCESSING_TRANSITION, RECALL, SAVING remain the same) ...
             case TASK_PHASES.READY_ASSESSMENT:
                return (
                    <div className="text-center">
                        <p className="text-lg font-semibold mb-4">Assessment Ready</p>
                        <p className="mb-6 text-gray-600">The main assessment will now begin.</p>
                        {/* Button removed, startAssessment triggered by useEffect */}
                        <LoadingSpinner message="Starting..."/>
                         {feedback && <p className="mt-4 text-sm text-blue-600">{feedback}</p>}
                    </div>
                );

            case TASK_PHASES.FIXATION:
                return (
                    <div className="text-4xl font-bold text-gray-700 h-80 flex items-center justify-center animate-pulse">
                        +
                    </div>
                );

            case TASK_PHASES.ODD_EVEN_JUDGMENT:
                return (
                    <div className="flex flex-col items-center w-full">
                        <div className="w-full min-h-[300px] mb-4 flex items-center justify-center">
                             {currentStimulus?.items ? (
                                <StimulusDisplay items={currentStimulus.items} />
                             ) : (
                                <LoadingSpinner message="Loading stimulus..." />
                             )}
                        </div>
                        <p className="mt-1 mb-3 text-base sm:text-lg font-medium text-gray-800">Is the count of <span className="font-bold text-blue-600">BLUE CIRCLES</span> Odd or Even?</p>
                        <div className="flex space-x-4">
                            <button
                                onClick={() => handleJudgmentClick(true)}
                                disabled={currentPhase !== TASK_PHASES.ODD_EVEN_JUDGMENT}
                                className="px-6 py-2 sm:px-8 sm:py-3 text-base sm:text-lg font-semibold rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            > Odd </button>
                            <button
                                onClick={() => handleJudgmentClick(false)}
                                disabled={currentPhase !== TASK_PHASES.ODD_EVEN_JUDGMENT}
                                className="px-6 py-2 sm:px-8 sm:py-3 text-base sm:text-lg font-semibold rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            > Even </button>
                        </div>
                    </div>
                );

            case TASK_PHASES.PROCESSING_TRANSITION:
                return <div className="h-80 w-full"></div>; // Blank screen ITI

            case TASK_PHASES.RECALL:
                if (setSize > 0) {
                    return <RecallInterface
                                setSize={setSize}
                                onSubmit={handleRecallSubmit}
                                value={intermediateRecallSequence}
                                onChange={handleRecallChange}
                           />;
                }
                console.error(`Cannot render RecallInterface: Invalid set size (${setSize}) in phase ${currentPhase}`);
                return ( <p className="text-red-600">Error: Cannot display recall interface.</p> );

            case TASK_PHASES.SAVING:
                 // Handled by the global isLoading check at the top
                 return <LoadingSpinner message="Saving results..." />;

            case TASK_PHASES.RESULTS:
                const resultsData = {
                    scorePCU: finalScorePCU,
                    maxScorePCU: 75,
                    processingAccuracy: finalProcessingAccuracy,
                    accuracyThreshold: 85,
                    flaggedLowAccuracy: flaggedLowAccuracy,
                    isFamiliarization: isFamiliarization,
                    error: error, // Pass potential saving errors to results screen
                };
                return <CountingSpanResults
                            results={resultsData}
                            onDone={() => navigate('/testing/counting-span', { state: { justUpdatedCst: true } })}
                       />;

            default:
                console.warn(`Rendering fallback for phase: ${currentPhase}`);
                return ( <div className="text-center text-gray-500"> <p>Initializing Task...</p> <LoadingSpinner /> </div> );
        }
    };

    // --- Main component layout ---
    // Determine if exit button should be shown
    const showExitButton = !isLoading && currentPhase !== TASK_PHASES.RESULTS && currentPhase !== TASK_PHASES.SAVING && currentPhase !== TASK_PHASES.INSTRUCTIONS && currentPhase !== TASK_PHASES.READY_ASSESSMENT;

    return (
        <>
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-200 p-4 font-sans relative">

                 {/* --- EARLY EXIT BUTTON --- */}
                 {showExitButton && (
                     <button
                         onClick={openExitModal} // Open the confirmation modal
                         title="Exit Assessment Early"
                         className="absolute top-2 right-2 z-50 p-2 bg-gray-400 text-white text-xs rounded-full shadow hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                         <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                     </button>
                 )}
                 {/* --- END EARLY EXIT BUTTON --- */}

                 {/* --- DEVELOPMENT SKIP BUTTON --- */}
                 {isDevelopment && showExitButton && ( // Show skip only if exit is also shown
                     <button
                         onClick={handleDevSkipTask}
                         disabled={isLoading}
                         title="DEV ONLY: Skip to Results"
                         // Position below the exit button
                         className="absolute top-12 right-2 z-50 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                         <FontAwesomeIcon icon={faForward} className="mr-1" />
                         Skip
                     </button>
                 )}
                 {/* --- END DEVELOPMENT SKIP BUTTON --- */}

                 <div className="w-full max-w-3xl bg-white shadow-xl rounded-lg border border-gray-300 overflow-hidden">
                     {/* Optional Header Area displaying progress */}
                      {(currentPhase !== TASK_PHASES.READY_ASSESSMENT &&
                        currentPhase !== TASK_PHASES.RESULTS &&
                        currentPhase !== TASK_PHASES.SAVING &&
                        !isPractice) && (
                         <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs text-gray-500 text-center tracking-wide">
                             {/* Progress Info */}
                             {testStructure && testStructure.length > 0 && currentSetIndex >= 0 &&
                                 `Set: ${currentSetIndex + 1} / ${testStructure.length}`
                             }
                              {setSize > 0 && currentTrialIndexInSet >= 0 &&
                               (currentPhase === TASK_PHASES.ODD_EVEN_JUDGMENT || currentPhase === TASK_PHASES.PROCESSING_TRANSITION || currentPhase === TASK_PHASES.FIXATION) &&
                                 ` | Trial: ${currentTrialIndexInSet + 1} / ${setSize}`
                             }
                             {currentPhase === TASK_PHASES.RECALL && ` | Recalling ${setSize} items...`}
                             {/* Add indicator for familiarization if relevant */}
                             {isFamiliarization && <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Familiarization</span>}
                         </div>
                     )}

                     <div className="p-4 sm:p-6 min-h-[450px] flex flex-col justify-center items-center">
                        {renderContent()}
                     </div>

                </div>
            </div>

            {/* Render the Feedback Button */}
            <FeedbackButton />

            {/* --- Exit Confirmation Modal --- */}
            <ConfirmationModal
                isOpen={isExitModalOpen}
                onClose={closeExitModal}
                onConfirm={confirmExit}
                title="Confirm Exit"
                confirmText="Confirm Exit"
                cancelText="Cancel"
                confirmColor="red" // Use red for destructive action
                isDestructive={true}
                // No input required for this confirmation
                requiredInputText={null}
            >
                <p className="text-sm text-gray-700 mb-2">
                    Are you sure you want to exit the assessment?
                </p>
                <p className="text-sm font-semibold text-red-600">
                    Your current progress will be saved as your final score for this session, and you will not be able to resume or retake it immediately.
                </p>
                {/* Add specific message about familiarization vs monthly */}
                {isFamiliarization ? (
                     <p className="text-sm text-gray-600 mt-2">
                         This is a <span className="font-medium">familiarization session</span>. Exiting early will still consume this practice attempt.
                     </p>
                 ) : (
                     <p className="text-sm text-gray-600 mt-2">
                         This is your <span className="font-medium">monthly assessment</span>. Exiting early will record your current score and you won't be able to take another assessment this month.
                     </p>
                 )}
            </ConfirmationModal>
            {/* --- End Exit Confirmation Modal --- */}
        </>
    );
};

export default CountingSpanTask;